import { clients } from '../../data/mock';
import { useInView } from '../../hooks/useInView';
import { Building, Factory, Fuel, Zap, Landmark, Train, Flame, Truck } from 'lucide-react';

// Client categories with icons for visual differentiation
const clientCategories = {
  "BHEL": { icon: Factory, color: "bg-blue-600" },
  "GAIL": { icon: Fuel, color: "bg-emerald-600" },
  "SAIL": { icon: Factory, color: "bg-slate-700" },
  "ONGC": { icon: Flame, color: "bg-orange-600" },
  "Reliance": { icon: Building, color: "bg-blue-700" },
  "Indian Oil": { icon: Fuel, color: "bg-red-600" },
  "HP": { icon: Fuel, color: "bg-green-700" },
  "BSES": { icon: Zap, color: "bg-amber-600" },
  "Vizag Steel": { icon: Factory, color: "bg-indigo-700" },
  "Aditya Birla": { icon: Building, color: "bg-red-700" },
  "UltraTech": { icon: Factory, color: "bg-gray-700" },
  "NALCO": { icon: Factory, color: "bg-teal-600" },
  "Delhi Metro": { icon: Train, color: "bg-sky-600" },
  "NTPC": { icon: Zap, color: "bg-green-600" },
  "Power Grid": { icon: Zap, color: "bg-yellow-600" },
  "Tata Steel": { icon: Factory, color: "bg-blue-800" },
};

export const ClientsSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section id="clients" className="py-20 lg:py-28 bg-gradient-to-b from-navy-50/30 to-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-14 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="section-divider" />
            <span className="text-sm font-semibold text-ind-red-600 uppercase tracking-wider">Our Clients</span>
            <div className="section-divider" />
          </div>
          <h2 className="font-heading font-extrabold text-navy-950 text-3xl lg:text-4xl mb-3">
            Trusted by Industry Leaders Across India
          </h2>
          <p className="text-navy-500 text-base max-w-xl mx-auto">
            Serving India's most demanding industries — power, steel, oil & gas, infrastructure — with quality products and reliable delivery.
          </p>
        </div>

        {/* Client Logos Grid - Professional styled badges */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 lg:gap-4">
          {clients.map((client, idx) => {
            const clientData = clientCategories[client] || { icon: Building, color: "bg-navy-600" };
            const IconComponent = clientData.icon;
            
            return (
              <div
                key={client}
                className={`group relative flex flex-col items-center justify-center bg-white border border-navy-100 rounded-xl p-3 lg:p-4 h-24 hover:border-ind-red-300 hover:shadow-lg hover:shadow-ind-red-100/50 transition-all duration-300 ${
                  isInView ? 'animate-fade-in-up' : 'opacity-0'
                }`}
                style={{ animationDelay: `${Math.min(idx * 0.05, 0.6)}s` }}
              >
                {/* Icon badge */}
                <div className={`w-10 h-10 rounded-lg ${clientData.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-5 h-5 text-white" />
                </div>
                {/* Client name */}
                <p className="font-heading font-bold text-xs text-navy-700 group-hover:text-navy-900 leading-tight text-center">{client}</p>
              </div>
            );
          })}
        </div>

        {/* Trust indicators */}
        <div className={`mt-12 flex flex-wrap justify-center gap-6 lg:gap-12 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.8s' }}>
          <div className="flex items-center gap-2 text-navy-500">
            <Landmark className="w-4 h-4" />
            <span className="text-sm font-medium">Government PSUs</span>
          </div>
          <div className="flex items-center gap-2 text-navy-500">
            <Factory className="w-4 h-4" />
            <span className="text-sm font-medium">Steel & Manufacturing</span>
          </div>
          <div className="flex items-center gap-2 text-navy-500">
            <Zap className="w-4 h-4" />
            <span className="text-sm font-medium">Power & Energy</span>
          </div>
          <div className="flex items-center gap-2 text-navy-500">
            <Truck className="w-4 h-4" />
            <span className="text-sm font-medium">Infrastructure</span>
          </div>
        </div>
      </div>
    </section>
  );
};
